import { normalizeRoomCode, ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH } from "@playpal/core";
import { useEffect, useRef } from "react";

interface Props {
	value: string;
	onChange: (value: string) => void;
	onComplete?: (value: string) => void;
	autoFocus?: boolean;
}

/**
 * Four big boxes rather than one text field: it shows how many letters to
 * expect, jumps forward on its own, and takes a pasted code in one go.
 */
export function CodeEntry({ value, onChange, onComplete, autoFocus }: Props) {
	const inputs = useRef<(HTMLInputElement | null)[]>([]);
	const letters = Array.from({ length: ROOM_CODE_LENGTH }, (_, i) => value[i] ?? "");

	useEffect(() => {
		if (autoFocus) inputs.current[0]?.focus();
	}, [autoFocus]);

	const setAt = (index: number, raw: string) => {
		const cleaned = normalizeRoomCode(raw);
		// A paste fills the whole code from wherever it landed.
		if (cleaned.length > 1) {
			const next = normalizeRoomCode(value.slice(0, index) + cleaned);
			onChange(next);
			const landed = Math.min(next.length, ROOM_CODE_LENGTH - 1);
			inputs.current[landed]?.focus();
			if (next.length === ROOM_CODE_LENGTH) onComplete?.(next);
			return;
		}

		const chars = letters.slice();
		chars[index] = cleaned;
		const next = chars.join("").slice(0, ROOM_CODE_LENGTH);
		onChange(next);

		if (cleaned && index < ROOM_CODE_LENGTH - 1) inputs.current[index + 1]?.focus();
		if (next.length === ROOM_CODE_LENGTH) onComplete?.(next);
	};

	const onKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Backspace" && !letters[index] && index > 0) {
			event.preventDefault();
			const chars = letters.slice();
			chars[index - 1] = "";
			onChange(chars.join(""));
			inputs.current[index - 1]?.focus();
		}
		if (event.key === "ArrowLeft" && index > 0) inputs.current[index - 1]?.focus();
		if (event.key === "ArrowRight" && index < ROOM_CODE_LENGTH - 1)
			inputs.current[index + 1]?.focus();
	};

	return (
		<div className="flex w-full justify-center gap-1.5 sm:gap-3">
			{letters.map((letter, index) => (
				<input
					// Fixed-length code: the position is the identity.
					key={index}
					ref={(element) => {
						inputs.current[index] = element;
					}}
					value={letter}
					onChange={(event) => setAt(index, event.target.value)}
					onKeyDown={(event) => onKeyDown(index, event)}
					onFocus={(event) => event.target.select()}
					inputMode="text"
					autoCapitalize="characters"
					autoComplete="off"
					spellCheck={false}
					maxLength={ROOM_CODE_LENGTH}
					aria-label={`Code letter ${index + 1} of ${ROOM_CODE_LENGTH}`}
					pattern={`[${ROOM_CODE_ALPHABET}]`}
					className="h-14 min-w-0 flex-1 rounded-xl bg-white/85 text-center text-2xl font-black uppercase shadow-inner transition focus:scale-105 sm:h-20 sm:flex-none sm:w-16 sm:rounded-2xl sm:text-5xl dark:bg-black/25"
				/>
			))}
		</div>
	);
}
